import {
  Box,
  Container,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useGetAllAbsenceRequestsQuery, useRevokeAbsenceRequestMutation } from "../../api/absenceRequestsApi";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { ViewAbsenceRequestDialog } from "./components/ViewAbsenceRequestDialog";
import AllRequestsList from "./components/AllRequestsList";
import { AbsenceRequestResponseDto } from "../../types/absence";
import { Snackbar } from "@mui/material";
import { useInfiniteDataTableItems } from "../../hooks/useInfiniteDataTableItems";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";

const AdminAllRequestsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [requestToView, setRequestToView] = useState<AbsenceRequestResponseDto | null>(null);
  const [requestToRevoke, setRequestToRevoke] = useState<AbsenceRequestResponseDto | null>(null);
  const [revocationReason, setRevocationReason] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { data: requestsData, isLoading, isFetching, error } = useGetAllAbsenceRequestsQuery(
    {
      paginationParams: {
        pageNumber: page + 1,
        pageSize: DEFAULT_DATA_TABLE_PAGE_SIZE,
      },
    }
  );
  const { accumulatedItems: requests, resetAccumulatedItems } =
    useInfiniteDataTableItems<AbsenceRequestResponseDto>(requestsData?.items, page);
  const [revokeRequest, { isLoading: isRevoking }] = useRevokeAbsenceRequestMutation();

  const resetRequestsTable = () => {
    resetAccumulatedItems();
    setPage(0);
  };

  const handleViewDetails = (request: AbsenceRequestResponseDto) => {
    setRequestToView(request);
  };

  const handleRevokeClick = (request: AbsenceRequestResponseDto) => {
    setRequestToRevoke(request);
  };

  const handleConfirmRevoke = async () => {
    if (!requestToRevoke) return;

    try {
      await revokeRequest({
        id: requestToRevoke.id,
        dto: { revocationReason: revocationReason || undefined }
      }).unwrap();
      setSuccessMessage("Absence request revoked successfully");
      setRequestToRevoke(null);
      setRevocationReason("");
      resetRequestsTable();
    } catch (error) {
      console.error("Failed to revoke request:", error);
      setRequestToRevoke(null);
      setRevocationReason("");
    }
  };

  const handleCancelRevoke = () => {
    setRequestToRevoke(null);
    setRevocationReason("");
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const handleLoadMore = () => {
    if (requestsData?.hasNextPage && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  if (isLoading && !requestsData) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load absence requests. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Results */}
      {requestsData?.items && requestsData.items.length === 0 ? (
        <Alert severity="info">
          No absence requests found.
        </Alert>
      ) : (
        <AllRequestsList
          requests={requests}
          isLoading={isLoading && requests.length === 0}
          onViewDetailsClick={handleViewDetails}
          onRevokeClick={handleRevokeClick}
          totalCount={requestsData?.totalCount}
          hasNextPage={requestsData?.hasNextPage}
          isLoadingMore={isFetching && requests.length > 0}
          onLoadMore={handleLoadMore}
        />
      )}

      {/* View Request Details Dialog */}
      <ViewAbsenceRequestDialog
        open={!!requestToView}
        request={requestToView}
        onClose={() => setRequestToView(null)}
      />

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={!!requestToRevoke}
        onClose={handleCancelRevoke}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke Absence Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to revoke this absence request? This action will:
          </Typography>
          <Typography component="ul" variant="body2" sx={{ mb: 2, pl: 2 }}>
            <li>Return the days to the employee's balance</li>
            <li>Send a notification email to the employee</li>
            <li>Mark the request as "Revoked"</li>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Revocation Reason (Optional)"
            placeholder="Enter the reason for revoking this request..."
            value={revocationReason}
            onChange={(e) => setRevocationReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRevoke} disabled={isRevoking}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRevoke}
            variant="contained"
            color="error"
            disabled={isRevoking}
          >
            {isRevoking ? "Revoking..." : "Revoke Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
};

export default AdminAllRequestsPage;

